import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
  Badge,
  Tooltip,
  Collapse,
  Card,
  CardContent,
} from '@mui/material';
import logo from '../Transparent.png';
import transparentPng from '../Transparent.png';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  School,
  Class,
  Chat,
  Assessment,
  AccountCircle,
  ExitToApp,
  CalendarToday,
  Assignment,
  Payment,
  Notifications,
  Settings,
  Close,
  ChevronLeft,
  ExpandLess,
  ExpandMore,
  Quiz,
  EventAvailable,
  Groups,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import NotificationBell from './NotificationBell';

// Responsive drawer widths
const drawerWidths = {
  desktop: 280,
  tablet: 240,
  mobile: 280,
};

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedMenus, setExpandedMenus] = useState({});

  const drawerWidth = isMobile ? drawerWidths.mobile : isTablet ? drawerWidths.tablet : drawerWidths.desktop;

  // Close mobile drawer when screen size changes
  useEffect(() => {
    if (!isMobile && mobileOpen) {
      setMobileOpen(false);
    }
  }, [isMobile, mobileOpen]);

  const getMenuItems = () => {
    if (!user) {
      return [{ text: 'Dashboard', icon: <Dashboard />, path: '/admin/dashboard' }];
    }

    if (user.userType === 'admin') {
      return [
        { text: 'Dashboard', icon: <Dashboard />, path: '/admin/dashboard' },
        {
          text: 'User Management',
          icon: <People />,
          children: [
            { text: 'Students', icon: <People />, path: '/admin/students' },
            { text: 'Teachers', icon: <School />, path: '/admin/teachers' },
          ]
        },
        { text: 'Trial Requests', icon: <Assignment />, path: '/admin/trial-requests' },
        { text: 'Class Scheduler', icon: <Class />, path: '/admin/classes' },
        { text: 'Group Classes', icon: <Groups />, path: '/admin/group-classes' },
        { text: 'Calendar', icon: <CalendarToday />, path: '/admin/calendar' },
        { text: 'Re-Schedule Requests', icon: <EventAvailable />, path: '/admin/reschedule-requests' },

        { text: 'Chat Management', icon: <Chat />, path: '/admin/chats' },
        // { text: 'Reports', icon: <Assessment />, path: '/admin/reports' },
        { text: 'Finance', icon: <Payment />, path: '/admin/finance' },
      ];
    } else if (user.userType === 'teacher') {
      return [
        { text: 'Dashboard', icon: <Dashboard />, path: '/teacher/dashboard' },
        { text: 'My Classes', icon: <Class />, path: '/teacher/classes' },
        { text: 'Calendar', icon: <CalendarToday />, path: '/teacher/calendar' },

        { text: 'Quick Tests', icon: <Quiz />, path: '/teacher/quick-test?create=true' },
        { text: 'Homework', icon: <Assignment />, path: '/teacher/homework' },
        { text: 'Attendance', icon: <Assessment />, path: '/teacher/attendance' },
        { text: 'Students', icon: <People />, path: '/teacher/students' },
        { text: 'Chat', icon: <Chat />, path: '/teacher/chat' },
        { text: 'Reports', icon: <Assessment />, path: '/teacher/reports' },
      ];
    } else if (user.userType === 'student') {
      return [
        { text: 'Dashboard', icon: <Dashboard />, path: '/student/dashboard' },
        { text: 'My Classes', icon: <Class />, path: '/student/classes' },
        { text: 'Calendar', icon: <CalendarToday />, path: '/student/calendar' },
        { text: 'Quick Tests', icon: <Quiz />, path: '/student/quick-test' },
        { text: 'Homework', icon: <Assignment />, path: '/student/homework' },
        { text: 'Teachers', icon: <School />, path: '/student/teachers' },
        { text: 'Attendance', icon: <Assessment />, path: '/student/attendance' },

        { text: 'Chat', icon: <Chat />, path: '/student/chat' },


        { text: 'Invoices', icon: <Payment />, path: '/student/invoices' },

      ];
    }

    return [{ text: 'Dashboard', icon: <Dashboard />, path: '/admin/dashboard' }];
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleMenuClose();
  };

  const handleMenuExpand = (menuKey) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  const renderMenuItem = (item, index) => {
    if (item.children) {
      const isExpanded = expandedMenus[item.text];
      return (
        <Box key={item.text}>
          <ListItem
            button
            onClick={() => handleMenuExpand(item.text)}
            sx={{
              borderRadius: '12px',
              margin: '2px 8px',
              '&:hover': {
                backgroundColor: 'primary.light',
                '& .MuiListItemIcon-root': {
                  color: 'primary.contrastText',
                },
                '& .MuiListItemText-primary': {
                  color: 'primary.contrastText',
                },
                '& .MuiSvgIcon-root': {
                  color: 'primary.contrastText',
                },
              },
            }}
          >
            <ListItemIcon sx={{ color: 'primary.main' }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                fontSize: isMobile ? '0.9rem' : '1rem',
                fontWeight: 500,
              }}
            />
            {isExpanded ? <ExpandLess sx={{ color: 'primary.main' }} /> : <ExpandMore sx={{ color: 'primary.main' }} />}
          </ListItem>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map((child) => (
                <ListItem
                  key={child.text}
                  button
                  selected={location.pathname === child.path}
                  onClick={() => {
                    navigate(child.path);
                    if (isMobile) setMobileOpen(false);
                  }}
                  sx={{
                    pl: 4,
                    borderRadius: '12px',
                    margin: '2px 16px',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      '& .MuiListItemIcon-root': {
                        color: 'primary.contrastText',
                      },
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    },
                    '&:hover': {
                      backgroundColor: 'primary.light',
                      '& .MuiListItemIcon-root': {
                        color: 'primary.contrastText',
                      },
                      '& .MuiListItemText-primary': {
                        color: 'primary.contrastText',
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: 'primary.main' }}>{child.icon}</ListItemIcon>
                  <ListItemText
                    primary={child.text}
                    primaryTypographyProps={{
                      fontSize: isMobile ? '0.85rem' : '0.9rem',
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Collapse>
        </Box>
      );
    }

    return (
      <ListItem
        key={item.text}
        button
        selected={location.pathname === item.path}
        onClick={() => {
          navigate(item.path);
          if (isMobile) setMobileOpen(false);
        }}
        sx={{
          borderRadius: '12px',
          margin: '2px 8px',
          '&.Mui-selected': {
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            '& .MuiListItemIcon-root': {
              color: 'primary.contrastText',
            },
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          },
          '&:hover': {
            backgroundColor: 'primary.light',
            '& .MuiListItemIcon-root': {
              color: 'primary.contrastText',
            },
            '& .MuiListItemText-primary': {
              color: 'primary.contrastText',
            },
          },
        }}
      >
        <ListItemIcon sx={{ color: 'primary.main' }}>{item.icon}</ListItemIcon>
        <ListItemText
          primary={item.text}
          primaryTypographyProps={{
            fontSize: isMobile ? '0.9rem' : '1rem',
            fontWeight: 500,
          }}
        />
      </ListItem>
    );
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          minHeight: 64,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, ml: '20%' }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <img
              src={logo}
              alt="EduMetrix Logo"
              style={{
                width: '120%',
                height: '120%',
                objectFit: 'contain',
              }}
            />
          </Box>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              color: 'text.primary',
              fontWeight: 700,
              fontSize: isMobile ? '1.32rem' : '1.5rem',
            }}
          >
            EduMetrix
          </Typography>
        </Box>
        {isMobile && (
          <IconButton onClick={handleDrawerToggle}>
            <Close />
          </IconButton>
        )}
      </Box>

      {/* User Info Card */}
      <Box sx={{ p: 2 }}>
        <Card
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: '16px',
          }}
        >
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={(() => {
                  if (user?.userType === 'admin') {
                    return transparentPng;
                  }
                  if (!user?.profile?.profilePicture) {
                    return undefined;
                  }
                  // Check if it's a data URL
                  if (user.profile.profilePicture.startsWith('data:')) {
                    return user.profile.profilePicture;
                  }
                  // Check if it's a filename with extension
                  if (/\.(jpg|jpeg|png|gif|webp)$/i.test(user.profile.profilePicture)) {
                    return `${process.env.REACT_APP_SOCKET_URL || 'https://backend.edumetrix.uk'}/uploads/${user.profile.profilePicture}`;
                  }
                  // Otherwise treat as base64
                  return `data:${user.profile.profilePictureType || 'image/jpeg'};base64,${user.profile.profilePicture}`;
                })()}
                sx={{
                  width: 48,
                  height: 48,
                  backgroundColor:
                    user?.userType === 'admin'
                      ? 'transparent'
                      : 'rgba(255, 255, 255, 0.2)',
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                }}
              >
                {user?.userType !== 'admin' && !user?.profile?.profilePicture
                  ? (user?.profile?.name ||
                    user?.profile?.studentName ||
                    user?.username)
                    ?.charAt(0)
                    ?.toUpperCase()
                  : ''}
              </Avatar>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    fontSize: isMobile ? '0.9rem' : '1rem',
                    lineHeight: 1.2,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user?.profile?.name || user?.profile?.studentName || user?.username || 'Please Login'}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.9,
                    fontSize: isMobile ? '0.75rem' : '0.8rem',
                    textTransform: 'capitalize',
                  }}
                >
                  {user?.userType || 'not authenticated'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 1 }}>
        <List sx={{ py: 1 }}>
          {getMenuItems().map((item, index) => renderMenuItem(item, index))}
        </List>
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        {/* Footer content removed - Settings moved to profile dropdown */}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: 'background.paper',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
          backdropFilter: 'blur(8px)',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 2,
              display: { md: 'none' },
              borderRadius: '12px',
            }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              flexGrow: 1,
              fontWeight: 600,
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
            }}
          >
            {user?.userType === 'admin' ? 'EduMetrix' :
              user?.userType === 'teacher' ? 'Teacher Dashboard' :
                user?.userType === 'student' ? 'Student Dashboard' : 'Dashboard'}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Notifications */}
            <NotificationBell userType={user?.userType} />

            {/* Settings */}
            <Tooltip title="Settings">
              <IconButton
                color="inherit"
                onClick={() => navigate(`/${user?.userType || 'admin'}/settings`)}
                sx={{ borderRadius: '12px' }}
              >
                <Settings />
              </IconButton>
            </Tooltip>

            {/* User name - hide on mobile */}
            <Typography
              variant="body2"
              sx={{
                mr: 1,
                display: { xs: 'none', sm: 'block' },
                fontWeight: 500,
              }}
            >
              {user?.profile?.name || user?.profile?.studentName || user?.username || 'Please Login'}
            </Typography>

            {/* User menu */}
            <Tooltip title="Account">
              <IconButton
                color="inherit"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{ borderRadius: '12px' }}
              >
                <Avatar
                  src={(() => {
                    if (user?.userType === 'admin') {
                      return transparentPng;
                    }
                    if (!user?.profile?.profilePicture) {
                      return undefined;
                    }
                    // Check if it's a data URL
                    if (user.profile.profilePicture.startsWith('data:')) {
                      return user.profile.profilePicture;
                    }
                    // Check if it's a filename with extension
                    if (/\.(jpg|jpeg|png|gif|webp)$/i.test(user.profile.profilePicture)) {
                      return `${process.env.REACT_APP_SOCKET_URL || 'https://backend.edumetrix.uk'}/uploads/${user.profile.profilePicture}`;
                    }
                    // Otherwise treat as base64
                    return `data:${user.profile.profilePictureType || 'image/jpeg'};base64,${user.profile.profilePicture}`;
                  })()}
                  sx={{
                    width: { xs: 32, sm: 36 },
                    height: { xs: 32, sm: 36 },
                    backgroundColor: user?.userType === 'admin' && 'transparent',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                  }}
                >
                  {user?.userType !== 'admin' && !user?.profile?.profilePicture ? (user?.profile?.name || user?.profile?.studentName || user?.username)?.charAt(0)?.toUpperCase() || 'G' : ''}
                </Avatar>
              </IconButton>
            </Tooltip>

            {/* User Menu */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                sx: {
                  borderRadius: '12px',
                  mt: 1,
                  minWidth: 200,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                },
              }}
            >
              {
                user?.userType === 'admin' && (
                  <MenuItem
                    onClick={() => {
                      navigate('/admin/profile');

                      handleMenuClose();
                    }}
                    sx={{ borderRadius: '8px', mx: 1, my: 0.5 }}
                  >
                    <AccountCircle sx={{ mr: 2 }} /> Profile
                  </MenuItem>
                )
              }


              <Divider sx={{ my: 1 }} />
              <MenuItem
                onClick={handleLogout}
                sx={{
                  borderRadius: '8px',
                  mx: 1,
                  my: 0.5,
                  color: 'error.main',
                }}
              >
                <ExitToApp sx={{ mr: 2 }} /> Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRadius: '0 16px 16px 0',
              border: 'none',
              boxShadow: '8px 0 32px rgba(0, 0, 0, 0.12)',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: 'background.default',
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }} />
        <Box
          sx={{
            p: { xs: 2, sm: 3, lg: 4 },
            maxWidth: '100%',
            overflow: 'hidden',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;